/**
 * Live cursor presence for open proposal structure cards.
 *
 * Each viewer writes `proposalStructurePresence/{roomId}/presence/{uid}`
 * with cursor position in normalized content coordinates (0–100).
 */
import { db } from './firebase';
import {
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';
import { BOARD_PRESENCE_STALE_MS, cursorColorForUid } from './boardPresenceService';
import { pointerToPageNorm } from './documentPresenceService';

export { cursorColorForUid, BOARD_PRESENCE_STALE_MS as STRUCTURE_PRESENCE_STALE_MS, pointerToPageNorm };

export const STRUCTURE_PRESENCE_THROTTLE_MS = 150;

export function proposalStructurePresenceRoomId(dealId: string, nodeSetId: string): string {
  const deal = (dealId || '').trim() || '_unknown';
  const node = (nodeSetId || '').trim() || '_unknown';
  return `${deal}__${node}`.replace(/\//g, '__').slice(0, 700);
}

export interface StructureCursorPresence {
  uid: string;
  name: string;
  color: string;
  /** Normalized content X (0–100). */
  x: number;
  /** Normalized content Y (0–100). */
  y: number;
  updatedAt: Date | null;
}

function presenceDoc(dealId: string, nodeSetId: string, uid: string) {
  return doc(
    db,
    'proposalStructurePresence',
    proposalStructurePresenceRoomId(dealId, nodeSetId),
    'presence',
    uid,
  );
}

export function isFreshStructureCursor(c: StructureCursorPresence, now = Date.now()): boolean {
  return !!c.updatedAt && now - c.updatedAt.getTime() < BOARD_PRESENCE_STALE_MS;
}

/** All peer cursors (self excluded). Staleness is re-checked by the caller. */
export function subscribeStructurePresence(
  dealId: string,
  nodeSetId: string,
  selfUid: string,
  callback: (cursors: StructureCursorPresence[]) => void,
): () => void {
  return onSnapshot(
    collection(
      db,
      'proposalStructurePresence',
      proposalStructurePresenceRoomId(dealId, nodeSetId),
      'presence',
    ),
    snap => {
      const cursors = snap.docs
        .map(d => {
          const v = d.data();
          const updatedAtRaw = v.updatedAt as { toDate?: () => Date } | undefined;
          return {
            uid: d.id,
            name: String(v.name || 'Someone'),
            color: String(v.color || '#6366f1'),
            x: Number(v.x) || 0,
            y: Number(v.y) || 0,
            updatedAt: updatedAtRaw?.toDate?.() ?? null,
          };
        })
        .filter(c => c.uid !== selfUid);
      callback(cursors);
    },
    err => {
      console.warn('[proposalStructurePresence] subscribe failed', err);
      callback([]);
    },
  );
}

export async function publishStructureCursor(
  dealId: string,
  nodeSetId: string,
  cursor: {
    uid: string;
    name: string;
    color: string;
    x: number;
    y: number;
  },
): Promise<void> {
  try {
    await setDoc(
      presenceDoc(dealId, nodeSetId, cursor.uid),
      {
        name: cursor.name,
        color: cursor.color,
        x: Math.round(cursor.x * 10) / 10,
        y: Math.round(cursor.y * 10) / 10,
        updatedAt: serverTimestamp(),
      },
      { merge: true },
    );
  } catch (err) {
    console.warn('[proposalStructurePresence] publish failed', err);
  }
}

export async function clearStructureCursor(
  dealId: string,
  nodeSetId: string,
  uid: string,
): Promise<void> {
  try {
    await deleteDoc(presenceDoc(dealId, nodeSetId, uid));
  } catch {
    /* stale-filtering handles missed deletes */
  }
}
